import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Mail, Phone, MessageSquare, CheckCircle, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type DeliveryMethod = "email" | "sms" | "phone_call";

interface VerificationModalProps {
  open: boolean;
  onClose: () => void;
  onVerified: () => void;
  userEmail: string | null;
  userPhone: string | null;
}

export function VerificationModal({
  open,
  onClose,
  onVerified,
  userEmail,
  userPhone,
}: VerificationModalProps) {
  const [step, setStep] = useState<"select" | "verify">("select");
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>("email");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [codeId, setCodeId] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep("select");
      setDeliveryMethod("email");
      setVerificationCode("");
      setCodeId(null);
      setError(null);
      setCountdown(0);
    }
  }, [open]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const generateCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendVerificationCode = async () => {
    setIsSending(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("You must be logged in to verify");
      }

      const code = generateCode();
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString(); // 10 minutes

      // Determine destination based on delivery method
      const destination = deliveryMethod === "email" ? userEmail : userPhone;
      if (!destination) {
        throw new Error(`No ${deliveryMethod === "email" ? "email" : "phone"} available for verification`);
      }

      // Store the verification code in the database
      const { data: codeData, error: insertError } = await supabase
        .from("verification_codes")
        .insert({
          user_id: session.user.id,
          code,
          delivery_method: deliveryMethod,
          destination,
          purpose: "intake_verification",
          expires_at: expiresAt,
        })
        .select("id")
        .single();

      if (insertError) throw insertError;
      setCodeId(codeData.id);

      // Determine which edge function to call
      let functionName: string;
      let payload: Record<string, string>;

      if (deliveryMethod === "email") {
        functionName = "send-verification-email";
        payload = { email: destination, code, purpose: "intake_verification" };
      } else if (deliveryMethod === "sms") {
        functionName = "send-verification-sms";
        payload = { phone: destination, code, purpose: "intake_verification" };
      } else {
        functionName = "send-verification-call";
        payload = { phone: destination, code, purpose: "intake_verification" };
      }

      // Call the edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Failed to send verification code");
      }

      setStep("verify");
      setCountdown(60); // 60 second cooldown for resend
      
      const methodText = deliveryMethod === "email" ? "email" : deliveryMethod === "sms" ? "SMS" : "phone call";
      toast.success(`Verification code sent via ${methodText}`);
    } catch (err: any) {
      console.error("Error sending verification code:", err);
      setError(err.message || "Failed to send verification code");
      toast.error(err.message || "Failed to send verification code");
    } finally {
      setIsSending(false);
    }
  };

  const verifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("You must be logged in to verify");
      }

      // Fetch the verification code from the database
      const { data: codeData, error: fetchError } = await supabase
        .from("verification_codes")
        .select("*")
        .eq("id", codeId)
        .single();

      if (fetchError) throw fetchError;

      // Check if code is expired
      if (new Date(codeData.expires_at) < new Date()) {
        throw new Error("Verification code has expired. Please request a new one.");
      }

      // Check max attempts
      if (codeData.attempts >= codeData.max_attempts) {
        throw new Error("Maximum verification attempts exceeded. Please request a new code.");
      }

      // Update attempts count
      await supabase
        .from("verification_codes")
        .update({ attempts: (codeData.attempts || 0) + 1 })
        .eq("id", codeId);

      // Verify the code
      if (codeData.code !== verificationCode) {
        const remaining = codeData.max_attempts - (codeData.attempts || 0) - 1;
        throw new Error(`Invalid code. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`);
      }

      // Mark as verified
      await supabase
        .from("verification_codes")
        .update({ 
          is_verified: true,
          verified_at: new Date().toISOString()
        })
        .eq("id", codeId);

      toast.success("Verification successful!");
      onVerified();
    } catch (err: any) {
      console.error("Error verifying code:", err);
      setError(err.message || "Failed to verify code");
    } finally {
      setIsVerifying(false);
    }
  };

  const getDeliveryMethodIcon = (method: DeliveryMethod) => {
    switch (method) {
      case "email":
        return <Mail className="h-5 w-5" />;
      case "sms":
        return <MessageSquare className="h-5 w-5" />;
      case "phone_call":
        return <Phone className="h-5 w-5" />;
    }
  };

  const getDeliveryMethodLabel = (method: DeliveryMethod) => {
    switch (method) {
      case "email":
        return "Email";
      case "sms":
        return "Text Message (SMS)";
      case "phone_call":
        return "Phone Call";
    }
  };

  const getMaskedDestination = (method: DeliveryMethod) => {
    if (method === "email" && userEmail) {
      const [local, domain] = userEmail.split("@");
      return `${local.slice(0, 2)}***@${domain}`;
    } else if ((method === "sms" || method === "phone_call") && userPhone) {
      const digits = userPhone.replace(/\D/g, "");
      return `(***) ***-${digits.slice(-4)}`;
    }
    return "Not available";
  };

  const isMethodAvailable = (method: DeliveryMethod) => {
    if (method === "email") return !!userEmail;
    return !!userPhone;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === "verify" && <CheckCircle className="h-5 w-5 text-primary" />}
            {step === "select" ? "Verify Your Identity" : "Enter Verification Code"}
          </DialogTitle>
          <DialogDescription>
            {step === "select"
              ? "Before submitting your intake form, we need to verify your identity. Choose how you'd like to receive your verification code."
              : `Enter the 6-digit code sent to ${getMaskedDestination(deliveryMethod)}`}
          </DialogDescription>
        </DialogHeader>

        {step === "select" ? (
          <div className="space-y-6 py-4">
            <RadioGroup
              value={deliveryMethod}
              onValueChange={(v) => setDeliveryMethod(v as DeliveryMethod)}
              className="space-y-3"
            >
              {(["email", "sms", "phone_call"] as DeliveryMethod[]).map((method) => (
                <div key={method} className="flex items-center space-x-3">
                  <RadioGroupItem 
                    value={method} 
                    id={method} 
                    disabled={!isMethodAvailable(method)}
                  />
                  <Label 
                    htmlFor={method} 
                    className={`flex items-center gap-3 cursor-pointer ${!isMethodAvailable(method) ? 'opacity-50' : ''}`}
                  >
                    {getDeliveryMethodIcon(method)}
                    <div>
                      <div className="font-medium">{getDeliveryMethodLabel(method)}</div>
                      <div className="text-sm text-muted-foreground">
                        {getMaskedDestination(method)}
                      </div>
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {error && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={onClose} disabled={isSending}>
                Cancel
              </Button>
              <Button 
                onClick={sendVerificationCode} 
                disabled={isSending || !isMethodAvailable(deliveryMethod)}
              >
                {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Code
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setVerificationCode(value);
                  setError(null);
                }}
                className="text-center text-2xl tracking-widest font-mono"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            <div className="text-center text-sm text-muted-foreground">
              Didn't receive the code?{" "}
              {countdown > 0 ? (
                <span>Resend in {countdown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={() => setStep("select")}
                  className="text-primary hover:underline"
                >
                  Try again
                </button>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setStep("select")} disabled={isVerifying}>
                Back
              </Button>
              <Button 
                onClick={verifyCode} 
                disabled={isVerifying || verificationCode.length !== 6}
              >
                {isVerifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
